/**
 * Configure the errors pass (Phase 6) - agent_config key 'errors_pass'. Toggle which
 * error types are active and set a severity override per type. See
 * worker/src/compliance/prompts.ts#errorsUserContent and runner.ts for how this flows
 * into the compliance pipeline.
 */
import { useEffect, useState } from 'react';
import { fetchAgentConfig } from '../../admin/queries';
import { updateAgentConfig, ApiError } from '../../api';
import { Banner, Button, Tile } from '../../components/ui';

const ERROR_TYPES = ['spelling', 'math', 'locality', 'other'] as const;
const SEVERITIES = ['low', 'medium', 'high'] as const;
type ErrorType = (typeof ERROR_TYPES)[number];
type Severity = (typeof SEVERITIES)[number];

const LABELS: Record<ErrorType, string> = {
  spelling: 'Spelling',
  math: 'Math',
  locality: 'Locality',
  other: 'Other',
};

export function ErrorsConfigAdmin() {
  const [enabled, setEnabled] = useState<Record<ErrorType, boolean>>({ spelling: true, math: true, locality: true, other: true });
  const [overrides, setOverrides] = useState<Partial<Record<ErrorType, Severity>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const row = await fetchAgentConfig('errors_pass');
        const value = row?.value ?? {};
        const enabledTypes = Array.isArray(value['enabled_types']) ? (value['enabled_types'] as string[]) : ERROR_TYPES;
        setEnabled({
          spelling: enabledTypes.includes('spelling'),
          math: enabledTypes.includes('math'),
          locality: enabledTypes.includes('locality'),
          other: enabledTypes.includes('other'),
        });
        const severityOverrides = (value['severity_overrides'] ?? {}) as Partial<Record<ErrorType, Severity>>;
        setOverrides(severityOverrides);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the errors pass config.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const enabledTypes = ERROR_TYPES.filter((t) => enabled[t]);
      await updateAgentConfig('errors_pass', { enabled_types: enabledTypes, severity_overrides: overrides });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the errors pass config.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Tile>Loading…</Tile>;

  return (
    <div className="stack">
      {error ? <Banner tone="error">{error}</Banner> : null}
      {saved ? <Banner tone="good">Saved.</Banner> : null}

      <Tile className="admin-card">
        <h3 className="checklist-cat__title">Active error types</h3>
        <div className="admin-rows">
          {ERROR_TYPES.map((type) => (
            <div className="admin-row" key={type}>
              <div className="admin-row__fields">
                <label className="admin-row__toggle">
                  <input
                    type="checkbox"
                    checked={enabled[type]}
                    onChange={(e) => setEnabled((prev) => ({ ...prev, [type]: e.target.checked }))}
                  />
                  {LABELS[type]}
                </label>
                <label className="admin-row__toggle">
                  Severity override
                  <select
                    className="input"
                    value={overrides[type] ?? ''}
                    onChange={(e) =>
                      setOverrides((prev) => ({ ...prev, [type]: (e.target.value || undefined) as Severity | undefined }))
                    }
                  >
                    <option value="">None (model decides)</option>
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ))}
        </div>
        <Button onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Tile>
    </div>
  );
}
